import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Card,
  CardBody,
  Col,
  Container,
  Input,
  Label,
  Row,
  Spinner,
} from "reactstrap";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import Breadcrumbs from "../../components/Common/Breadcrumb";
import { VHU_LOTS_PAGE } from "../../routes/routeConstants";
import { isVehiclePhoto, resolveDocumentUrl } from "../database/documentUrl";
import { search as searchVehicules } from "../database/vehiculeService";
import { loadVhuLot, saveVhuLot } from "./vhuLotService";

const STATUS_OPTIONS = [
  { value: "BROUILLON", label: "Brouillon" },
  { value: "OUVERT", label: "Ouvert" },
  { value: "CLOTURE", label: "Cloture" },
  { value: "ANNULE", label: "Annule" },
];

const toDatetimeLocal = (date) => {
  const offsetDate = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
  return offsetDate.toISOString().slice(0, 16);
};

const normalizeDatetimeLocal = (value) => {
  if (!value) return "";
  const valueText = String(value);
  if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(valueText)) return valueText;
  const date = new Date(valueText);
  if (Number.isNaN(date.getTime())) return valueText;
  return toDatetimeLocal(date);
};

const createEmptyForm = () => {
  const now = new Date();
  const tomorrow = new Date(now);
  tomorrow.setDate(now.getDate() + 1);

  return {
    reference: "",
    titre: "",
    description: "",
    status: "OUVERT",
    dateDebut: toDatetimeLocal(now),
    dateFin: toDatetimeLocal(tomorrow),
    prixDepart: "",
    pasMinimal: "0",
    estimationDepart: "",
    lieuStockage: "",
    vehiculeIds: [],
    isActif: true,
  };
};

const asNumberOrNull = (value) => (value === "" || value === null || value === undefined ? null : Number(value));
const vehicleLabel = (vehicle) => [vehicle.marque, vehicle.modele, vehicle.immatriculation || vehicle.numChassis].filter(Boolean).join(" - ") || `Vehicule #${vehicle.id}`;
const vehiclePhotos = (vehicle) => ((vehicle?.photos || vehicle?.documents || []).filter(isVehiclePhoto));
const moneyLabel = (value) => `${Number(value || 0).toLocaleString("fr-FR")} MAD`;

const normalizeLotForForm = (lot) => ({
  ...createEmptyForm(),
  ...(lot || {}),
  dateDebut: normalizeDatetimeLocal(lot?.dateDebut),
  dateFin: normalizeDatetimeLocal(lot?.dateFin),
  prixDepart: lot?.prixDepart ?? "",
  pasMinimal: lot?.pasMinimal ?? "0",
  estimationDepart: lot?.estimationDepart ?? "",
  vehiculeIds: lot?.vehiculeIds || lot?.vehicules?.map((vehicle) => vehicle.id) || [],
  isActif: lot?.isActif !== false,
});

function VhuLotFormPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();
  const isEditMode = Boolean(id);
  const [form, setForm] = useState(() => normalizeLotForForm(location.state?.lot));
  const [vehicules, setVehicules] = useState([]);
  const [vehicleQuery, setVehicleQuery] = useState("");
  const [loading, setLoading] = useState(Boolean(id));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      setError(null);
      try {
        const requests = [searchVehicules({ criteria: {}, page: 0, size: 500, sortField: "createdDate", direction: "DESC" })];
        if (id && !location.state?.lot) requests.push(loadVhuLot(id));
        const [vehicleResponse, lotResponse] = await Promise.all(requests);
        setVehicules(vehicleResponse?.data?.body?.content || []);
        if (lotResponse) setForm(normalizeLotForForm(lotResponse?.data?.body));
      } catch (e) {
        setError(e?.response?.data?.message || "Impossible de charger le formulaire du lot.");
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [id, location.state?.lot]);

  const selectedVehicles = useMemo(() => {
    const selectedIds = new Set((form.vehiculeIds || []).map(Number));
    return vehicules.filter((vehicle) => selectedIds.has(Number(vehicle.id)));
  }, [form.vehiculeIds, vehicules]);

  const filteredVehicules = useMemo(() => {
    const query = vehicleQuery.trim().toLowerCase();
    if (!query) return vehicules;
    return vehicules.filter((vehicle) => vehicleLabel(vehicle).toLowerCase().includes(query));
  }, [vehicules, vehicleQuery]);

  const setField = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const toggleVehicle = (idValue) => {
    setForm((prev) => {
      const current = (prev.vehiculeIds || []).map(Number);
      const nextId = Number(idValue);
      return {
        ...prev,
        vehiculeIds: current.includes(nextId) ? current.filter((item) => item !== nextId) : [...current, nextId],
      };
    });
  };

  const renderVehicleThumb = (vehicle, size = 48) => {
    const photos = vehiclePhotos(vehicle);
    const photo = photos[0];
    if (!photo) {
      return (
        <span className="vhu-thumb vhu-thumb-empty" style={{ width: size, height: size }}>
          <i className="ri-car-line text-muted" />
        </span>
      );
    }
    return <img src={resolveDocumentUrl(photo)} alt={vehicleLabel(vehicle)} className="vhu-thumb" style={{ width: size, height: size }} />;
  };

  const submit = async (event) => {
    event.preventDefault();
    setSaving(true);
    setError(null);
    try {
      await saveVhuLot({
        ...form,
        id: isEditMode ? Number(id) : form.id,
        prixDepart: asNumberOrNull(form.prixDepart),
        pasMinimal: asNumberOrNull(form.pasMinimal),
        estimationDepart: asNumberOrNull(form.estimationDepart),
      });
      navigate(VHU_LOTS_PAGE, {
        state: { successMessage: isEditMode ? "Lot VHU mis a jour avec succes." : "Lot VHU cree avec succes." },
      });
    } catch (e) {
      setError(e?.response?.data?.message || "Impossible d'enregistrer le lot VHU.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="page-content auction-page vhu-lots-page vhu-lot-form-page">
      <Container fluid>
        <Breadcrumbs
          title={isEditMode ? "Modifier le lot VHU" : "Nouveau lot VHU"}
          breadcrumbItems={[{ title: "Modules", link: "#" }, { title: "Lots VHU", link: VHU_LOTS_PAGE }, { title: isEditMode ? "Modification" : "Creation", link: "#" }]}
        />

        {loading ? (
          <div className="text-center py-5"><Spinner color="primary" /></div>
        ) : (
          <form onSubmit={submit}>
            {error && <Alert color="danger">{error}</Alert>}
            <Row className="g-3">
              <Col xl={8}>
                <Card className="border-0 shadow-sm">
                  <CardBody>
                    <div className="vhu-section-label mb-3">Informations du lot</div>
                    <Row className="g-3">
                      <Col md={4}>
                        <Label>Reference</Label>
                        <Input value={form.reference || ""} onChange={(event) => setField("reference", event.target.value)} placeholder="VHU-2026-001" />
                      </Col>
                      <Col md={8}>
                        <Label>Titre</Label>
                        <Input value={form.titre || ""} onChange={(event) => setField("titre", event.target.value)} placeholder="Lot utilitaires sinistres" required />
                      </Col>
                      <Col md={4}>
                        <Label>Status</Label>
                        <Input type="select" value={form.status} onChange={(event) => setField("status", event.target.value)}>
                          {STATUS_OPTIONS.map((item) => <option key={item.value} value={item.value}>{item.label}</option>)}
                        </Input>
                      </Col>
                      <Col md={4}>
                        <Label>Lieu stockage</Label>
                        <Input value={form.lieuStockage || ""} onChange={(event) => setField("lieuStockage", event.target.value)} placeholder="Depot Casablanca" />
                      </Col>
                      <Col md={4}>
                        <Label>Visibilite mobile</Label>
                        <div className="vhu-toggle-row">
                          <Input type="checkbox" checked={form.isActif !== false} onChange={(event) => setField("isActif", event.target.checked)} />
                          <span>Lot actif pour publication</span>
                        </div>
                      </Col>
                      <Col md={6}>
                        <Label>Date debut</Label>
                        <Input type="datetime-local" value={form.dateDebut || ""} onChange={(event) => setField("dateDebut", event.target.value)} />
                      </Col>
                      <Col md={6}>
                        <Label>Date fin</Label>
                        <Input type="datetime-local" value={form.dateFin || ""} onChange={(event) => setField("dateFin", event.target.value)} />
                      </Col>
                      <Col md={4}>
                        <Label>Prix depart</Label>
                        <Input type="number" min="0" value={form.prixDepart} onChange={(event) => setField("prixDepart", event.target.value)} />
                      </Col>
                      <Col md={4}>
                        <Label>Pas minimal</Label>
                        <Input type="number" min="0" value={form.pasMinimal} onChange={(event) => setField("pasMinimal", event.target.value)} />
                      </Col>
                      <Col md={4}>
                        <Label>Estimation</Label>
                        <Input type="number" min="0" value={form.estimationDepart} onChange={(event) => setField("estimationDepart", event.target.value)} />
                      </Col>
                      <Col xs={12}>
                        <Label>Description</Label>
                        <Input type="textarea" rows="4" value={form.description || ""} onChange={(event) => setField("description", event.target.value)} placeholder="Etat general, conditions de visite, contraintes d'enlevement..." />
                      </Col>
                    </Row>
                  </CardBody>
                </Card>

                <Card className="border-0 shadow-sm mt-3">
                  <CardBody>
                    <div className="vhu-section-label mb-3">Véhicules du lot</div>
                    <div className="vhu-search-control">
                      <i className="ri-search-line" />
                      <Input value={vehicleQuery} onChange={(event) => setVehicleQuery(event.target.value)} placeholder="Rechercher marque, modele, immatriculation..." />
                    </div>
                    <div className="vhu-vehicle-list vhu-vehicle-list-large">
                      {filteredVehicules.map((vehicle) => {
                        const selected = (form.vehiculeIds || []).map(Number).includes(Number(vehicle.id));
                        const photos = vehiclePhotos(vehicle);
                        return (
                          <button type="button" key={vehicle.id} className={`vhu-vehicle-option ${selected ? "active" : ""}`} onClick={() => toggleVehicle(vehicle.id)}>
                            <div className="d-flex gap-2 align-items-center">
                              {renderVehicleThumb(vehicle)}
                              <div className="flex-grow-1 min-w-0">
                                <div className="fw-semibold text-truncate">{vehicleLabel(vehicle)}</div>
                                <div className="small text-muted">{photos.length} photo(s) - {vehicle.carburant || "Carburant non renseigne"}</div>
                              </div>
                              {selected && <i className="ri-check-line fs-4 text-primary" />}
                            </div>
                          </button>
                        );
                      })}
                      {!filteredVehicules.length && <div className="vhu-empty-note">Aucun vehicule ne correspond a cette recherche.</div>}
                    </div>
                  </CardBody>
                </Card>
              </Col>

              <Col xl={4}>
                <Card className="border-0 shadow-sm vhu-form-summary">
                  <CardBody>
                    <div className="d-flex align-items-start justify-content-between gap-2 mb-3">
                      <div>
                        <div className="vhu-section-label mb-2">Resume</div>
                        <h5 className="mb-1">{form.titre || "Lot sans titre"}</h5>
                        <div className="text-muted small">{form.reference || "Reference automatique ou libre"}</div>
                      </div>
                      <Badge color={form.status === "OUVERT" ? "success" : "secondary"}>{form.status}</Badge>
                    </div>
                    <Row className="g-2 vhu-form-summary-metrics">
                      <Col xs={6}><span>Véhicules</span><strong>{selectedVehicles.length}</strong></Col>
                      <Col xs={6}><span>Prix depart</span><strong>{moneyLabel(form.prixDepart)}</strong></Col>
                      <Col xs={6}><span>Pas</span><strong>{moneyLabel(form.pasMinimal)}</strong></Col>
                      <Col xs={6}><span>Estimation</span><strong>{moneyLabel(form.estimationDepart)}</strong></Col>
                    </Row>
                    <div className="vhu-selected-strip mt-3">
                      <div className="fw-semibold mb-2">Selection</div>
                      <div className="vhu-selected-vehicles">
                        {selectedVehicles.map((vehicle) => (
                          <div key={vehicle.id} className="vhu-selected-row">
                            {renderVehicleThumb(vehicle, 42)}
                            <span className="text-truncate">{vehicleLabel(vehicle)}</span>
                          </div>
                        ))}
                        {!selectedVehicles.length && <div className="text-muted small">Aucun vehicule selectionne.</div>}
                      </div>
                    </div>
                    <div className="d-flex justify-content-end gap-2 mt-4">
                      <Button color="light" type="button" onClick={() => navigate(VHU_LOTS_PAGE)}>Annuler</Button>
                      <Button color="primary" type="submit" disabled={saving}>
                        {saving && <Spinner size="sm" className="me-2" />}
                        {isEditMode ? "Mettre a jour" : "Créer le lot"}
                      </Button>
                    </div>
                  </CardBody>
                </Card>
              </Col>
            </Row>
          </form>
        )}
      </Container>
    </div>
  );
}

export default VhuLotFormPage;
